import { useCallback, useContext, useState } from 'react';
import { useParams } from 'react-router';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  AlertColor,
  Badge,
  Skeleton,
  Tooltip,
  Typography
} from '@mui/material';

import { useQueryClient } from '@tanstack/react-query';
import queryString from 'query-string';

import Page from '@/components/Page';
import UpdatesDialog from '@/components/Updates';
import { evaluationsConfig } from '@/components/Updates/config/config';
import Alert from '@/components/alert/Alert';
import Button from '@/components/button';
import Dialog from '@/components/dialog';
import Header from '@/components/header';
import { EvaluationDetailsSteps } from '@/constants/driverSteps';
import {
  PostDetailsContext,
  PostDetailsProvider
} from '@/contexts/PostDetailsContext';
import { useDeleteEvaluationPosts } from '@/hooks/queries/evaluations';
import { useAppSelector } from '@/hooks/redux';
import { getPathWithLocalParams } from '@/hooks/urlParams';
import useAlert from '@/hooks/useAlert';
import useArrowKeyNavigation from '@/hooks/useArrowKeyNavigation';
import useDriver from '@/hooks/useDriver';
import useWorkspaceNavigate from '@/hooks/useWorkspaceNavigate';
import { URLParams } from '@/models/misc';
import { Updates } from '@/models/updates';
import getRoute from '@/utils/getRoute';
import NotFound from '@/views/misc/notFound';

import PostLayout from './PostLayout';

const PostContainer = () => {
  const navigate = useWorkspaceNavigate();
  const queryClient = useQueryClient();
  const { postId, evaluationId, data, isError, isLoading, resetState } =
    useContext(PostDetailsContext);
  const alert = useAlert();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const urlParams = queryString.parse(window.location.search);
  const previousPostId = data?.previousPostId;
  const nextPostId = data?.nextPostId;
  const updatesCount = data?.evaluation?.updates?.length || 0;
  const violation = data?.evaluation?.compliance.compliance === 'violation';
  const currentPost = data?.currentPosition;
  const totalPosts = data?.totalCount;
  const user = useAppSelector((state) => state.user.currentUser);

  const { mutate: deletePost, isPending: isDeleting } =
    useDeleteEvaluationPosts(
      (result) => {
        if (result?.status === 200) {
          alert.success('The post was successfully deleted');
          if (previousPostId) {
            navigate(
              getRoute.evaluations.POST(evaluationId, previousPostId, urlParams)
            );
          } else if (nextPostId) {
            navigate(
              getRoute.evaluations.POST(evaluationId, nextPostId, urlParams)
            );
          } else {
            navigate(
              getRoute.evaluations.DETAIL(evaluationId, urlParams as URLParams)
            );
          }
          queryClient.invalidateQueries({
            queryKey: ['post'],
            refetchType: 'all'
          });
          queryClient.invalidateQueries({
            queryKey: ['posts'],
            refetchType: 'all'
          });
          queryClient.invalidateQueries({
            queryKey: ['evaluation'],
            refetchType: 'all'
          });
          queryClient.invalidateQueries({
            queryKey: ['evaluations'],
            refetchType: 'all'
          });
        } else if (result?.response?.data?.message) {
          alert.error(result.response.data.message);
        }
        setIsDeleteDialogOpen(false);
      },
      () => {
        alert.error('Something went wrong...');
        setIsDeleteDialogOpen(false);
      }
    );

  const handleClickDeletePost = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleClickPreviousPost = () => {
    resetState();
    if (previousPostId) {
      navigate(
        getRoute.evaluations.POST(evaluationId, previousPostId, urlParams)
      );
    }
  };

  const handleClickNextPost = () => {
    resetState();
    if (nextPostId) {
      navigate(getRoute.evaluations.POST(evaluationId, nextPostId, urlParams));
    }
  };

  const handleConfirmDeletePost = () => {
    const payload = { evaluationId, postsIds: [postId] };
    deletePost(payload);
  };

  useArrowKeyNavigation({
    onLeft: () => handleClickPreviousPost(),
    onRight: () => handleClickNextPost()
  });

  const evaluation: Updates.Dialog | undefined = data
    ? {
        postId: Number(postId),
        evaluationId,
        creatorName: data.creator?.name,
        updates: data.evaluation?.updates || []
      }
    : undefined;

  useDriver({
    isLoading,
    steps: EvaluationDetailsSteps
  });

  const getTermsAlert = useCallback((): {
    severity: AlertColor | undefined;
    message: string;
    action: string;
  } => {
    if (!data?.createTime || data?.isExpired)
      return { severity: undefined, action: '', message: '' };

    const isViolation =
      data?.evaluation?.compliance?.compliance === 'violation';
    const termDate = isViolation
      ? new Date(user.workspaceStorageTerms.violation)
      : new Date(user.workspaceStorageTerms.general);

    const createTime = new Date(data.createTime);

    const diffInMs = createTime.getTime() - termDate.getTime();
    const daysUntilDeletion = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (daysUntilDeletion > 30 || daysUntilDeletion < 0)
      return { severity: undefined, action: '', message: '' };

    return {
      severity: 'warning',
      message:
        'This post will expire soon according to the data storage terms.',
      action:
        daysUntilDeletion === 0
          ? 'The content will expire later today.'
          : `${daysUntilDeletion} day(s) until expiration.`
    };
  }, [data, user]);

  const termsAlert = getTermsAlert();

  return (
    <Page
      title="Evaluation Detail"
      header={
        <Header
          breadcrumbsList={[
            {
              name: 'Evaluations',
              onClick: () =>
                navigate(getPathWithLocalParams(getRoute.evaluations.LIST()))
            },
            {
              name: (
                <>
                  {isError ? (
                    <Typography>Failed to load title</Typography>
                  ) : (
                    data?.evaluation.title || (
                      <Skeleton variant="text" width="200px" />
                    )
                  )}
                </>
              ),
              onClick: () =>
                navigate(
                  getPathWithLocalParams(
                    getRoute.evaluations.DETAIL(evaluationId)
                  )
                )
            }
          ]}
          actions={
            <>
              <Button
                disabled={isLoading || isError}
                color="error"
                onClick={handleClickDeletePost}
                size="small"
                text="Delete"
              />
              <Badge
                badgeContent={updatesCount}
                invisible={!violation}
                sx={{
                  '& .MuiBadge-badge': {
                    right: 4,
                    backgroundColor: 'secondary.main',
                    color: 'common.white'
                  }
                }}
              >
                <Button
                  id="evaluation-detail-changes"
                  disabled={!violation}
                  onClick={() => setIsModalOpen(true)}
                  size="small"
                  text="Changes"
                />
              </Badge>
              <Tooltip title="Left arrow key for previous post">
                <span>
                  <Button
                    disabled={!previousPostId || isError}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowLeft') handleClickPreviousPost();
                    }}
                    onClick={handleClickPreviousPost}
                    size="small"
                    startIcon={<ArrowBackIcon />}
                    text="Previous"
                  />
                </span>
              </Tooltip>
              <Tooltip title="Right arrow key for next post">
                <span>
                  <Button
                    id="evaluation-detail-next"
                    disabled={!nextPostId || isError}
                    endIcon={<ArrowForwardIcon />}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowRight') handleClickNextPost();
                    }}
                    onClick={handleClickNextPost}
                    size="small"
                    text="Next"
                  />
                </span>
              </Tooltip>
            </>
          }
          isSmallHeader
          title={
            isLoading ? (
              <Skeleton variant="text" width={50} />
            ) : (
              <Typography variant="body2">
                <strong>{currentPost}</strong> of {totalPosts}
              </Typography>
            )
          }
        />
      }
    >
      <>
        {termsAlert.message && (
          <Alert
            sx={{ alignItems: 'center', mb: 1 }}
            severity={termsAlert.severity}
            content={termsAlert.message}
            action={termsAlert.action}
          />
        )}
        <PostLayout />
        <Dialog
          title="Delete Post"
          onClose={isDeleting ? undefined : () => setIsDeleteDialogOpen(false)}
          dialogContent={
            <>
              <Typography>
                Are you sure you want to delete this post?
              </Typography>
              <Typography>You will not be able to recover it.</Typography>
            </>
          }
          open={isDeleteDialogOpen}
          actions={
            <>
              <Button
                variant="text"
                text="cancel"
                disabled={isDeleting}
                onClick={() => setIsDeleteDialogOpen(false)}
              />
              <Button
                color="error"
                text="delete"
                onClick={handleConfirmDeletePost}
                loading={isDeleting}
              />
            </>
          }
        />
        {evaluation && (
          <UpdatesDialog
            isOpen={isModalOpen}
            context={evaluation}
            updates={data}
            onClose={() => setIsModalOpen(false)}
            config={evaluationsConfig}
          />
        )}
      </>
    </Page>
  );
};

const PostDetails = () => {
  const { evaluationId, postId } = useParams();

  if (!evaluationId || !postId) return <NotFound />;

  return (
    <PostDetailsProvider
      postId={Number(postId)}
      evaluationId={Number(evaluationId)}
      urlParams={queryString.parse(window.location.search)}
    >
      <PostContainer />
    </PostDetailsProvider>
  );
};

export default PostDetails;
